import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Fingerprint, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useWebAuthn, isWebAuthnSupported, PASSKEY_PROMPT_DISMISSED_KEY } from '../lib/webauthn';
import { safeStorage } from '../lib/safeStorage';

/**
 * PasskeyEnrolmentDialog
 * One-time prompt shown after the FIRST successful password sign-in:
 *   "Skip the password next time? Use Face ID / Fingerprint."
 *
 * Hosted by LoginPage, controlled by `open` prop. We persist a "dismissed"
 * flag in safeStorage so we don't pester returning users — the user can also
 * always set up a passkey from Settings later.
 */
function PasskeyEnrolmentDialog({ open, onClose }) {
  const { busy, error, enrolPasskey } = useWebAuthn();
  const [done, setDone] = useState(false);

  const handleEnrol = async () => {
    try {
      // Default label: device-derived best-effort
      const label = guessDeviceLabel();
      await enrolPasskey({ label });
      setDone(true);
      toast.success('Passkey saved. Next time, just tap to sign in.');
      // Mark dismissed so we don't re-prompt
      safeStorage.setItem(PASSKEY_PROMPT_DISMISSED_KEY, 'enrolled');
      setTimeout(() => onClose?.(), 800);
    } catch (e) {
      // Errors already toasted by hook via `error` state
      toast.error(e.message || 'Could not set up passkey');
    }
  };

  const handleSkip = (forever = false) => {
    if (forever) {
      safeStorage.setItem(PASSKEY_PROMPT_DISMISSED_KEY, 'skipped');
    }
    onClose?.();
  };

  if (!isWebAuthnSupported()) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleSkip(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="passkey-enrolment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-950">
            <Fingerprint className="w-5 h-5" />
            Skip the password next time?
          </DialogTitle>
          <DialogDescription className="text-zinc-700 leading-relaxed pt-2">
            Set up Face ID / Fingerprint sign-in on this device. It's faster, safer, and
            works even when your password manager doesn't.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-700">
          <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="leading-relaxed">
            Your existing email and password still work: this just adds a one-tap option.
            You can remove the passkey anytime from Settings.
          </p>
        </div>

        {error && (
          <p className="text-xs text-red-600" data-testid="passkey-enrolment-error">{error}</p>
        )}
        {done && (
          <p className="text-xs text-emerald-600 font-medium">All set: passkey saved.</p>
        )}

        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={handleEnrol}
            disabled={busy || done}
            className="w-full bg-zinc-950 text-white hover:bg-zinc-800 h-11"
            data-testid="passkey-enrolment-confirm-btn"
          >
            <Fingerprint className="w-4 h-4 mr-2" />
            {busy ? 'Setting up…' : 'Set up Face ID / Fingerprint'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSkip(false)}
            disabled={busy}
            className="w-full h-10"
            data-testid="passkey-enrolment-not-now-btn"
          >
            Not now
          </Button>
          <button
            type="button"
            onClick={() => handleSkip(true)}
            disabled={busy}
            className="text-xs text-zinc-500 hover:text-zinc-700 underline-offset-2 hover:underline mt-1"
            data-testid="passkey-enrolment-never-btn"
          >
            Don't ask again
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function guessDeviceLabel() {
  const ua = navigator.userAgent || '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'Mac';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Windows/i.test(ua)) return 'Windows device';
  if (/Linux/i.test(ua)) return 'Linux device';
  return 'This device';
}

export default PasskeyEnrolmentDialog;
